import { toLower } from 'lodash'
import { toast } from 'react-toastify'

import { SMALL_CASE_LETTERS_NUMBERS_REGEX, URL_REGEX } from '~/constants'
import { EClientPath } from '~/models/api'
import { EAuthErrorName, ECognitoGroups } from '~/models/auth'

type TParams = {
  fn: () => Promise<void>
  finallyFn?: () => void
  errorFn?: (error: string, name?: EAuthErrorName) => void
}

const asyncTryCatch = async ({ fn, finallyFn, errorFn }: TParams) => {
  try {
    await fn()
  } catch (error) {
    if (errorFn) {
      const err = error as { name?: string; message?: string }
      let errorMessage = 'Something went wrong!... Please try again later.'
      if (err.name === EAuthErrorName.CodeMismatchException) {
        errorMessage = 'Code is invalid.'
      } else if (err.name === EAuthErrorName.ExpiredCodeException) {
        errorMessage = 'The code has expired. Please request a new one.'
      } else if (err instanceof Error) {
        errorMessage = err.message
      }
      errorFn(errorMessage, err.name as EAuthErrorName)
    } else {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Something went wrong!... Please try again later.',
      )
    }
  } finally {
    if (finallyFn) {
      finallyFn()
    }
  }
}

export const propertyOf = <T>(name: keyof T) => name

export default asyncTryCatch

export const formatWebsiteLink = (inputLink?: string): string => {
  if (!inputLink) return ''

  return inputLink.trim().replace(URL_REGEX, 'https://')
}

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>
    }
  : T

export const sanitizedBusinessName = (link: string) => {
  return toLower(link.replace(SMALL_CASE_LETTERS_NUMBERS_REGEX, ''))
}

export const getTierFriendlyName = (tier: ECognitoGroups) => {
  switch (tier) {
    case ECognitoGroups.customerBasicPlan:
      return 'Basic'
    case ECognitoGroups.customerPlusPlan:
      return 'Plus'
    case ECognitoGroups.customerFreePlan:
      return 'Free'
    case ECognitoGroups.customerBasicFreeTrialPlan:
      return 'Basic Free Trial'
    default:
      return 'N/A'
  }
}

export const addCookieTrackerScriptIfNeeded = () => {
  // InMobi Choice. Consent Manager Tag v3.0 (for TCF 2.2)
  const isScriptAdded = document.querySelector(
    'script[src="/js/cookieTracker.js"]',
  )

  const isChatPage = window.location.pathname.includes(EClientPath.chatBase)

  if (isScriptAdded || isChatPage) {
    return
  }

  const inmobiConsentManagerTag = document.createElement('script')
  inmobiConsentManagerTag.type = 'text/javascript'
  inmobiConsentManagerTag.async = true
  inmobiConsentManagerTag.src = '/js/cookieTracker.js'
  document.body.appendChild(inmobiConsentManagerTag)
}
